/**
 * Validácia emailu
 * @param {string} email - Email
 * @returns {boolean}
 */
export const validateEmail = (email) => {
  if (!email) return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validácia telefónneho čísla (slovenský alebo český formát)
 * @param {string} phone - Telefónne číslo
 * @returns {boolean}
 */
export const validatePhone = (phone) => {
  if (!phone) return false;
  
  // Slovenský/český formát: +421/+420, 00421/00420, 0..., alebo bez prefixu
  const phoneRegex = /^(?:\+421|\+420|00421|00420|0)?[0-9]{9}$/;
  const cleanPhone = phone.replace(/\s/g, '');
  
  return phoneRegex.test(cleanPhone);
};

/**
 * Validácia mena (min 2 znaky, len písmená a medzery)
 * @param {string} name - Meno
 * @returns {boolean}
 */
export const validateName = (name) => {
  if (!name || name.length < 2) return false;
  
  const nameRegex = /^[a-zA-ZáäčďéíĺľňóôŕšťúýžÁÄČĎÉÍĹĽŇÓÔŔŠŤÚÝŽ\s]+$/;
  return nameRegex.test(name);
};

/**
 * Validácia hesla (min 8 znakov, obsahuje veľké/malé písmeno a číslo)
 * @param {string} password - Heslo
 * @returns {boolean}
 */
export const validatePassword = (password) => {
  if (!password || password.length < 8) return false;
  
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  
  return hasUpperCase && hasLowerCase && hasNumber;
};

/**
 * Validácia povinného poľa
 * @param {any} value - Hodnota
 * @returns {boolean}
 */
export const validateRequired = (value) => {
  if (value === null || value === undefined) return false;
  if (typeof value === 'string') return value.trim().length > 0;
  return true;
};

/**
 * Formátuje telefónne číslo
 * @param {string} phone - Telefónne číslo
 * @returns {string} Formátované číslo
 */
export const formatPhone = (phone) => {
  if (!phone) return '';
  
  const cleaned = phone.replace(/\s/g, '');
  
  // Slovenský/český formát: +421/+420 XXX XXX XXX
  if (cleaned.startsWith('+421')) {
    const number = cleaned.substring(4);
    return `+421 ${number.substring(0, 3)} ${number.substring(3, 6)} ${number.substring(6)}`;
  }

  if (cleaned.startsWith('+420')) {
    const number = cleaned.substring(4);
    return `+420 ${number.substring(0, 3)} ${number.substring(3, 6)} ${number.substring(6)}`;
  }

  if (cleaned.startsWith('00421')) {
    const number = cleaned.substring(5);
    return `+421 ${number.substring(0, 3)} ${number.substring(3, 6)} ${number.substring(6)}`;
  }

  if (cleaned.startsWith('00420')) {
    const number = cleaned.substring(5);
    return `+420 ${number.substring(0, 3)} ${number.substring(3, 6)} ${number.substring(6)}`;
  }
  
  // Lokálny formát: 0XXX XXX XXX
  if (cleaned.startsWith('0')) {
    return `${cleaned.substring(0, 4)} ${cleaned.substring(4, 7)} ${cleaned.substring(7)}`;
  }
  
  return phone;
};

/**
 * Získa chybové hlášky pre formulár
 * @param {Object} values - Hodnoty formulára
 * @param {Array} requiredFields - Povinné polia
 * @returns {Object} Objekt s chybami
 */
export const getFormErrors = (values, requiredFields = []) => {
  const errors = {};
  
  // Kontrola povinných polí
  requiredFields.forEach(field => {
    if (!validateRequired(values[field])) {
      errors[field] = 'Toto pole je povinné';
    }
  });
  
  // Špecifické validácie
  if (values.email && !validateEmail(values.email)) {
    errors.email = 'Neplatný email';
  }
  
  if (values.telefon && !validatePhone(values.telefon)) {
    errors.telefon = 'Neplatné telefónne číslo';
  }
  
  if (values.cele_meno && !validateName(values.cele_meno)) {
    errors.cele_meno = 'Neplatné meno (min. 2 znaky, len písmená)';
  }
  
  if (values.heslo && !validatePassword(values.heslo)) {
    errors.heslo = 'Heslo musí mať min. 8 znakov, veľké/malé písmeno a číslo';
  }
  
  return errors;
};

/**
 * Kontroluje, či formulár má chyby
 * @param {Object} errors - Objekt s chybami
 * @returns {boolean}
 */
export const hasErrors = (errors) => {
  return Object.keys(errors).length > 0;
};

/**
 * Sanitizuje HTML (základná ochrana pred XSS)
 * @param {string} str - String
 * @returns {string} Sanitizovaný string
 */
export const sanitizeHtml = (str) => {
  if (!str) return '';
  
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return str.replace(/[&<>"'/]/g, (char) => map[char]);
};
